<template>
  <div>
    <v-row no-gutters class="mb-4">
      <v-col>
        <super-admin-module-sub-settings :settings="qgisPluginSettings" module="QGIS_PLUGIN" />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <super-admin-upload-row
          :is-enabled="qgisEnabled"
          :added="hasQgis"
          :data-input="computedInputQgis"
          :version="qgisVersion"
          :date-uploaded="qgisDateUploaded"
          @showUpload="showQgisUploadDialog"
        ></super-admin-upload-row>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <admin-wrapper
          :inputs="inputs"
          @save="saveInformation"
          :is-custom-saving="isSaving"
          :disabled="!qgisEnabled"
          disable-sticky-buttons
        >
          <template #[`qgisPluginInfoContent.input`]>
            <v-col cols="6">
              <tiptap-vuetify
                :disabled="!qgisEnabled"
                :extensions="extensions"
                v-model="content"
                :placeholder="$i18n.t('admin.infoPlaceholder')"
                :card-props="{ flat: true, outlined: true }"
                style="text-align: left"
              />
            </v-col>
          </template>
        </admin-wrapper>
      </v-col>
    </v-row>
    <main-large-dialog
      v-if="isQgisLoaded"
      :is-visible.sync="isQgisVisible"
      :title="$i18n.t('admin.newAPK')"
      agree-text="admin.upload"
      disagree-text="dialog.close"
      @agree="uploadQgis"
      dialog-width="28%"
      @modalClosed="onQgisClose"
    >
      <template #body>
        <admin-form :inputs="dialogInputsQgis" class="px-8 pb-10">
          <template #[`file.input`]="{dropzoneOptions}">
            <v-col cols="6">
              <file-input
                @successfullyAdded="successfullyAddedQgis"
                ref="fileQgis"
                :options="dropzoneOptions"
              ></file-input>
            </v-col>
          </template>
        </admin-form>
      </template>
    </main-large-dialog>
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';
import AdminForm from '@/components/AdminForm';
import AdminWrapper from '@/components/AdminWrapper';
import FileInput from '@/components/FileInput';
import SuperAdminModuleSubSettings from '@/components/SuperAdminModuleSubSettings';
import SuperAdminUploadRow from '@/components/SuperAdminUploadRow';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Paragraph,
  Heading,
  History,
  HardBreak,
  Link,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'QgisPluginAdmin',
  components: {
    TiptapVuetify,
    AdminWrapper,
    SuperAdminModuleSubSettings,
    AdminForm,
    FileInput,
    SuperAdminUploadRow,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
  },
  data: () => ({
    isQgisVisible: false,
    isQgisLoaded: false,
    dialogInputsQgis: [
      {
        name: 'file',
        translationPath: 'admin.file',
        align: 'start',
        isCustom: true,
        dropzoneOptions: {
          addRemoveLinks: true,
          url: `${process.env.VUE_APP_API_URL}/qgis_plugins`,
          method: 'post',
          autoProcessQueue: false,
          paramName: 'file',
          acceptedFiles: '.zip',
          maxFiles: 1,
        },
      },
    ],
    isSaving: false,
    content: '',
    extensions: [
      Bold,
      Italic,
      Underline,
      Paragraph,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3],
          },
        },
      ],
      HardBreak,
      Link,
      History,
      ListItem,
      BulletList,
      OrderedList,
    ],
  }),
  watch: {
    qgisEnabled(nV) {
      if (!nV) return;
      this.getQgisMetadata();
    },
  },
  computed: {
    qgisEnabled: get('admin/additionalModules@QGIS_PLUGIN.enabled'),
    hasQgis: get('admin/hasQgis'),
    qgisVersion: get('admin/qgisMetadata@version'),
    qgisDateUploaded: get('admin/qgisMetadata@date_uploaded'),
    qgisPluginInfoContent: get('admin/settingsValues@qgis_plugin_info_content_enabled'),
    qgisPluginSettings() {
      return [
        {
          name: 'qgis_plugin_gis_support_access_enabled',
          titlePath: 'admin.gisSupportQgisAccess',
          hideSubtitle: true,
        },
        {
          name: 'qgis_plugin_aquagis3_access_enabled',
          titlePath: 'admin.aquagisQgisAccess',
          hideSubtitle: true,
        },
      ];
    },
    inputs() {
      return [
        {
          name: 'qgisPluginInfoContent',
          translationPath: 'admin.informationContent',
          subtitle: 'admin.informationContentQgisSubtitle',
          isCustom: true,
          align: 'start',
        },
      ];
    },
    computedInputQgis() {
      if (this.hasQgis) {
        return {
          name: 'upload',
          translationPath: 'admin.qgisVersion',
          isCustom: true,
          isSuperadmin: true,
          isSubsection: true,
        };
      }
      return {
        name: 'upload',
        translationPath: 'admin.uploadQgis',
        isCustom: true,
        align: 'baseline',
        subtitle: 'admin.uploadQgisSubtitle',
        isSuperadmin: true,
      };
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    getQgisMetadata: call('admin/getQgisMetadata'),
    async saveInformation() {
      this.isSaving = true;
      await this.editSingleSetting({ key: 'qgis_plugin_info_content_enabled', body: { value: this.content } }),
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.setInformationSettings'),
        });
      this.content = this.qgisPluginInfoContent;
      this.isSaving = false;
    },
    showQgisUploadDialog() {
      this.isQgisVisible = true;
      this.isQgisLoaded = true;
    },
    async successfullyAddedQgis() {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message: this.$i18n.t('success.qgis'),
      });
      this.isQgisVisible = false;
      this.isQgisLoaded = false;
      this.$refs.fileQgis.$refs.dropzone.removeAllFiles();
      await this.getQgisMetadata();
    },
    uploadQgis() {
      this.$refs.fileQgis.$refs.dropzone.processQueue();
    },
    onQgisClose() {
      this.$refs.fileQgis.$refs.dropzone.removeAllFiles();
    },
    async init() {
      if (this.qgisEnabled) {
        await this.getQgisMetadata();
      }
      this.content = this.qgisPluginInfoContent;
    },
  },
  created() {
    this.init();
  },
};
</script>
